// Searchable Issuer field for the SecMaster Create / Update popups.
//
// Mirrors the type-to-search behaviour of the MA Watchlist filter's Issuer
// widget (watchlistFilterFields.jsx): a free-text Autocomplete that only starts
// matching after 2 characters and caps the dropdown at 200 results, so the very
// large issuer list never renders as an unusable plain Select. Visually it uses
// the popup's filled-variant `inputStyle` so it stays consistent with the other
// fields in the Add / Update Security popups.
import React, { useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { inputStyle } from "../OrderEntry/styles";
import { tokens } from "../../Common/designTokens";

const ISSUER_MIN_SEARCH_LENGTH = 2;
const ISSUER_MAX_RESULTS = 200;
const ISSUER_MAX_LENGTH = 50;

const issuerFilterOptions = createFilterOptions({
  ignoreCase: true,
  ignoreAccents: true,
  limit: ISSUER_MAX_RESULTS,
  stringify: (option) => `${option ?? ""}`,
  trim: true,
});

// fieldOptions.issuer arrives as [{ value, label }, ...]; the watchlist widget
// works on plain strings, so normalise to the option's value (falling back to
// label) and drop blanks/dupes.
const toIssuerStrings = (options) => {
  const seen = new Set();
  const result = [];
  (Array.isArray(options) ? options : []).forEach((opt) => {
    const str =
      typeof opt === "object" && opt !== null
        ? `${opt.value ?? opt.label ?? ""}`.trim()
        : `${opt ?? ""}`.trim();
    if (str && !seen.has(str)) {
      seen.add(str);
      result.push(str);
    }
  });
  return result;
};

const IssuerAutocomplete = ({
  label,
  value,
  onChange,
  options,
  error = false,
  helperText,
  disabled = false,
}) => {
  const issuerOptions = toIssuerStrings(options);
  const currentValue = value || "";

  // MUI shows the clear (X) icon only while the field is focused or hovered; at
  // rest just the dropdown arrow is visible. Reserve the wider room (both icons)
  // only when the clear icon can appear, so the value isn't ellipsised early in
  // the perfect resting state but never collides with the X when active.
  const [focused, setFocused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const reserveForClear = focused || hovered;

  // Filled-variant Autocomplete doesn't inherit the compact metrics of the plain
  // filled fields, so re-apply them (mirrors the OrderEntry dealer field). The
  // right padding only reserves room for the visible icons so the value gets the
  // full remaining width before ellipsising.
  const issuerAutocompleteSx = {
    ...inputStyle,
    "& .MuiFilledInput-root.MuiAutocomplete-inputRoot": {
      ...inputStyle["& .MuiFilledInput-root"],
      paddingTop: "10px !important",
      paddingRight: `${reserveForClear ? 76 : 52}px !important`,
      paddingBottom: "0 !important",
      paddingLeft: "0 !important",
      minHeight: "auto",
      height: "auto",
      "&.Mui-disabled": {
        ...(inputStyle["& .MuiFilledInput-root"]["&.Mui-disabled"] || {}),
        backgroundColor: tokens.colors.disabledWhite,
      },
    },
    "& .MuiAutocomplete-inputRoot .MuiAutocomplete-input": {
      padding: "14px 0 14px 12px !important",
      minWidth: 0,
      textOverflow: "ellipsis",
    },
    "& .MuiAutocomplete-endAdornment": {
      top: "50%",
      transform: "translateY(-50%)",
      right: "8px",
      marginLeft: 0,
      display: "flex",
      gap: 0,
    },
    "& .MuiAutocomplete-clearIndicator": { padding: "4px", marginRight: 0 },
    "& .MuiAutocomplete-popupIndicator": { padding: "4px" },
    "& .MuiAutocomplete-inputRoot": { flexWrap: "nowrap" },
  };

  return (
    <Autocomplete
      value={currentValue}
      inputValue={currentValue}
      disabled={disabled}
      fullWidth
      sx={issuerAutocompleteSx}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onInputChange={(_, newValue) =>
        onChange(newValue.slice(0, ISSUER_MAX_LENGTH))
      }
      onChange={(_, newValue) => onChange((newValue || "").slice(0, ISSUER_MAX_LENGTH))}
      options={issuerOptions}
      getOptionLabel={(option) => `${option ?? ""}`}
      isOptionEqualToValue={(option, val) => `${option ?? ""}` === `${val ?? ""}`}
      filterOptions={(opts, state) => {
        const searchValue = `${state.inputValue || currentValue || ""}`.trim();
        if (searchValue.length < ISSUER_MIN_SEARCH_LENGTH) return [];
        return issuerFilterOptions(opts, { ...state, inputValue: searchValue });
      }}
      openOnFocus
      selectOnFocus
      clearOnBlur={false}
      handleHomeEndKeys
      noOptionsText={
        `${currentValue}`.trim().length < ISSUER_MIN_SEARCH_LENGTH
          ? "Type at least 2 characters"
          : "No issuers found"
      }
      renderInput={(params) => (
        <TextField
          {...params}
          variant="filled"
          label={label}
          error={error}
          helperText={helperText || undefined}
          inputProps={{ ...params.inputProps, maxLength: ISSUER_MAX_LENGTH }}
          InputProps={{ ...params.InputProps, disableUnderline: true }}
        />
      )}
    />
  );
};

export default IssuerAutocomplete;
